import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { useLayoutStyleStore } from '../../stores/layoutStyleStore';
import { NewWorkspaceDialog } from './NewWorkspaceDialog';
import { RenameWorkspaceDialog } from './RenameWorkspaceDialog';
import { DeleteWorkspaceDialog } from './DeleteWorkspaceDialog';
import { WorkspaceId } from '../../../types';
import * as styles from './WorkspaceTabs.css.ts';

export const WorkspaceTabs: React.FC = () => {
  const project = useProjectStore((state) => state.project);
  const switchWorkspace = useProjectStore((state) => state.switchWorkspace);
  const moveWorkspace = useProjectStore((state) => state.moveWorkspace);

  const workspaces = project?.workspaces ?? [];
  const activeWorkspaceId = project?.activeWorkspaceId;

  const [contextMenu, setContextMenu] = useState<{ workspaceId: WorkspaceId; x: number; y: number } | null>(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<{ id: WorkspaceId; name: string } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // 初回マウント時にスタイルストアを初期化
  useEffect(() => {
    useLayoutStyleStore.getState().initializeStyles();
  }, []);

  // コンテキストメニュー外クリックで閉じる + 位置調整
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(null);
      }
    };

    if (contextMenu && contextMenuRef.current) {
      // メニューの実際のサイズを取得
      const menuRect = contextMenuRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      let adjustedX = contextMenu.x;
      let adjustedY = contextMenu.y;

      // 右端からはみ出す場合
      if (adjustedX + menuRect.width > windowWidth) {
        adjustedX = windowWidth - menuRect.width - 10;
      }

      // 下端からはみ出す場合、メニューの左下角をクリック位置に合わせる
      if (adjustedY + menuRect.height > windowHeight) {
        adjustedY = contextMenu.y - menuRect.height;
        // 上端からもはみ出さないように調整
        if (adjustedY < 0) {
          adjustedY = 10;
        }
      }

      // 位置を更新
      contextMenuRef.current.style.left = `${adjustedX}px`;
      contextMenuRef.current.style.top = `${adjustedY}px`;

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [contextMenu]);

  const handleTabClick = (workspaceId: WorkspaceId) => {
    if (workspaceId !== activeWorkspaceId) {
      switchWorkspace(workspaceId);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, workspaceId: WorkspaceId) => {
    e.preventDefault();
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (workspace) {
      setSelectedWorkspace({ id: workspace.id, name: workspace.name });

      // シンプルに初期位置を設定（実際の調整はuseEffectで行う）
      setContextMenu({ workspaceId, x: e.clientX, y: e.clientY });
    }
  };

  const handleAddWorkspace = () => {
    setShowNewDialog(true);
  };

  const handleRenameWorkspace = () => {
    setShowRenameDialog(true);
    setContextMenu(null);
  };

  const handleDeleteWorkspace = () => {
    setShowDeleteDialog(true);
    setContextMenu(null);
  };

  const handleMoveWorkspace = (direction: 'left' | 'right') => {
    if (contextMenu) {
      moveWorkspace(contextMenu.workspaceId, direction);
      setContextMenu(null);
    }
  };

  // ワークスペースの並び順をチェックして、移動可能性を判定
  const canMoveLeft = contextMenu
    ? workspaces.findIndex((w) => w.id === contextMenu.workspaceId) > 0
    : false;
  const canMoveRight = contextMenu
    ? workspaces.findIndex((w) => w.id === contextMenu.workspaceId) < workspaces.length - 1
    : false;

  return (
    <div className={styles.workspaceTabs}>
      <div className={styles.tabList}>
        {workspaces.map((workspace) => (
          <div
            key={workspace.id}
            className={`${styles.tab} ${workspace.id === activeWorkspaceId ? styles.active : ''}`}
            onClick={() => handleTabClick(workspace.id)}
            onContextMenu={(e) => handleContextMenu(e, workspace.id)}
            data-testid={`workspace-tab-${workspace.id}`}
            aria-selected={workspace.id === activeWorkspaceId}
          >
            <span
              className={styles.tabName}
              data-testid={`workspace-tab-span-${workspace.id}`}
              data-workspace-name={workspace.name}
            >
              {workspace.name}
            </span>
          </div>
        ))}
        <button
          className={styles.addButton}
          onClick={handleAddWorkspace}
          title="Add workspace"
          aria-label="Add workspace"
        >
          <Plus size={16} />
        </button>
      </div>

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className={styles.contextMenu}
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button className={styles.contextMenuButton} onClick={handleRenameWorkspace}>Rename</button>
          <button className={styles.contextMenuButton} onClick={() => handleMoveWorkspace('left')} disabled={!canMoveLeft}>
            Move Left
          </button>
          <button className={styles.contextMenuButton} onClick={() => handleMoveWorkspace('right')} disabled={!canMoveRight}>
            Move Right
          </button>
          <div className={styles.separator} />
          <button
            onClick={handleDeleteWorkspace}
            className={`${styles.contextMenuButton} ${styles.danger}`}
            disabled={workspaces.length <= 1}
          >
            Delete
          </button>
        </div>
      )}

      <NewWorkspaceDialog isOpen={showNewDialog} onClose={() => setShowNewDialog(false)} />

      {selectedWorkspace && (
        <>
          <RenameWorkspaceDialog
            isOpen={showRenameDialog}
            workspaceId={selectedWorkspace.id}
            currentName={selectedWorkspace.name}
            onClose={() => setShowRenameDialog(false)}
          />

          <DeleteWorkspaceDialog
            isOpen={showDeleteDialog}
            workspaceId={selectedWorkspace.id}
            workspaceName={selectedWorkspace.name}
            onClose={() => setShowDeleteDialog(false)}
          />
        </>
      )}
    </div>
  );
};
